import { Grid, Stack } from '@mui/material'
import { CarrierVisitDirection, OrderStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { OrderHoldsSection } from '@planning/components/hold/organisms/OrderHoldsSection'
import { OrderHoldsSectionStore } from '@planning/components/hold/organisms/stores/OrderHoldsSectionStore'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { ChangeTransportSubFormStore } from '@planning/components/organisms/ChangeTransportSubForm'
import holdService from '@planning/services/holdService'
import { scrollToFirstMuiError } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { FormStore, useRegisterSubFormStore } from '@tom-ui/utils'
import { comparer, reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { DirectionalCargoDetailsType } from '../../V2/organisms/DirectionalCargoDetailsSubForm'
import { CargoDetailsSubFormStore } from '../../V2/stores/CargoDetailsSubFormStore'
import { OrderFormStore, OrderFormStoreType } from '../../V2/stores/OrderFormStore'
import {
  ContainerCardData,
  UnitAdditionalAttributes,
} from '../StepperForm/molecules/UnitContainerCard'
import {
  OrderCargoViewCard,
  OrderCargoViewCardAttributes,
} from '../StepperForm/organisms/OrderCargoViewCard'
import {
  OrderTransportViewCard,
  OrderTransportViewCardAttributes,
} from '../StepperForm/organisms/OrderTransportViewCard'
import { OrderUnitViewCard } from '../StepperForm/organisms/OrderUnitViewCard'

type Props = {
  orderId: number
  containerJourneyId?: number | null
  hasServiceOrders: boolean
  formStore: OrderFormStore
  displayAttributes: OrderViewDisplayAttributes
  initialValues: Partial<OrderFormStoreType>
  hideActions?: boolean
  cargoFormIntoEditMode?: boolean
  onSubmit: () => Promise<void>
  setCargoFormIntoEditMode: (isEditMode: boolean) => void
  holdsStore: OrderHoldsSectionStore
}

export type OrderViewDisplayAttributes = {
  unitAdditionalAttributes?: UnitAdditionalAttributes
  containerAttributes: ContainerCardData
  transportAttributes: OrderTransportViewCardAttributes
  cargoAttributes: {
    commonCargoAttributes: OrderCargoViewCardAttributes
    inboundCargoAttributes: OrderCargoViewCardAttributes
    outboundCargoAttributes: OrderCargoViewCardAttributes
  }
}

export const ReviewForm = observer((props: Props) => {
  const {
    containerJourneyId,
    hasServiceOrders,
    formStore,
    initialValues,
    displayAttributes,
    hideActions,
    onSubmit,
    cargoFormIntoEditMode,
    setCargoFormIntoEditMode,
    holdsStore,
  } = props
  const { unitAdditionalAttributes, containerAttributes, transportAttributes, cargoAttributes } =
    displayAttributes
  const { t } = useTranslate()
  //First cargo details level
  const inboundCargoDetailsFormStore = useRegisterSubFormStore(
    formStore,
    'inboundCargoDetails',
    new FormStore<DirectionalCargoDetailsType>(),
  )

  const outboundCargoDetailsFormStore = useRegisterSubFormStore(
    formStore,
    'outboundCargoDetails',
    new FormStore<DirectionalCargoDetailsType>(),
  )

  const commonCargoDetailsFormStore = useRegisterSubFormStore(
    formStore,
    'cargoDetails',
    new FormStore<DirectionalCargoDetailsType>(),
  )

  //Second cargo details level
  const inboundCargoDetailsCargoFormStore = hasServiceOrders
    ? useRegisterSubFormStore(
        inboundCargoDetailsFormStore,
        'cargoDetails',
        new CargoDetailsSubFormStore(initialValues?.inboundCargoDetails?.cargoDetails),
      )
    : undefined

  const outboundCargoDetailsCargoFormStore = hasServiceOrders
    ? useRegisterSubFormStore(
        outboundCargoDetailsFormStore,
        'cargoDetails',
        new CargoDetailsSubFormStore(initialValues?.outboundCargoDetails?.cargoDetails),
      )
    : undefined

  const commonCargoDetailsCargoFormStore = useRegisterSubFormStore(
    commonCargoDetailsFormStore,
    'cargoDetails',
    new CargoDetailsSubFormStore(initialValues?.cargoDetails?.cargoDetails),
  )

  //Transport
  const inboundTransportStore = useRegisterSubFormStore(
    inboundCargoDetailsFormStore,
    'transport',
    new ChangeTransportSubFormStore(initialValues?.inboundCargoDetails?.transport),
  )
  inboundTransportStore.setContainerNumberAndDirection(
    containerAttributes.number,
    CarrierVisitDirection.Inbound,
  )

  const outboundTransportStore = useRegisterSubFormStore(
    outboundCargoDetailsFormStore,
    'transport',
    new ChangeTransportSubFormStore(initialValues?.outboundCargoDetails?.transport),
  )
  outboundTransportStore.setContainerNumberAndDirection(
    containerAttributes.number,
    CarrierVisitDirection.Outbound,
  )

  const validateAndFocusOnError = (store: { validate: () => boolean }) => {
    const isValid = store.validate()
    if (!isValid) {
      setTimeout(scrollToFirstMuiError, 0)
    }
    return isValid
  }

  const isInboundOrderFulfilled =
    unitAdditionalAttributes?.inboundOrder?.status === OrderStatus.Fulfilled

  const handleSaveTransport = async () => {
    const isInboundValid = isInboundOrderFulfilled
      ? true
      : validateAndFocusOnError(inboundTransportStore)
    const isOutboundValid = validateAndFocusOnError(outboundTransportStore)

    if (isInboundValid && isOutboundValid) {
      await onSubmit()
    }
  }

  const handleSaveCommonCargo = async () => {
    const isFormStoreValid = validateAndFocusOnError(formStore)
    const isCommonCargoDetailsValid = validateAndFocusOnError(commonCargoDetailsFormStore)

    if (isFormStoreValid && isCommonCargoDetailsValid) {
      await onSubmit()
    }
  }

  const handleSaveInboundCargo = async () => {
    const isFormStoreValid = validateAndFocusOnError(formStore)
    const isInboundCargoDetailsValid = validateAndFocusOnError(inboundCargoDetailsFormStore)

    if (isFormStoreValid && isInboundCargoDetailsValid) {
      await onSubmit()
    }
  }

  const handleSaveOutboundCargo = async () => {
    const isFormStoreValid = validateAndFocusOnError(formStore)
    const isOutboundCargoDetailsValid = validateAndFocusOnError(outboundCargoDetailsFormStore)

    if (isFormStoreValid && isOutboundCargoDetailsValid) {
      await onSubmit()
    }
  }

  const isDeparted = unitAdditionalAttributes?.journey?.status === ContainerJourneyStatus.Departed

  const { holdItemStore } = usePlanningStore()

  const reloadHolds = useCallback(async () => {
    if (!containerJourneyId) return
    const holds = await holdService.getByContainerJourneyId(containerJourneyId)
    await holdsStore.fetchHoldsAuditTrail(containerJourneyId)
    holdItemStore.upsertBulk(holds)
  }, [containerJourneyId, holdItemStore, holdsStore])

  useEffect(() => {
    if (!containerJourneyId) return

    if (!holdsStore.isAuditTrailFetched) {
      reloadHolds()
    }

    return reaction(
      () => holdItemStore.holdsByContainerJourneyId[containerJourneyId] ?? [],
      activeHolds => {
        holdsStore.loadFromDtos(activeHolds)
        void holdsStore.fetchHoldsAuditTrail(containerJourneyId)
      },
      { fireImmediately: true, equals: comparer.structural },
    )
  }, [containerJourneyId, holdsStore, holdItemStore, reloadHolds])

  return (
    <Stack gap={2}>
      <OrderUnitViewCard
        hideActions
        onClickSave={onSubmit}
        unitAdditionalAttributes={unitAdditionalAttributes}
        container={containerAttributes}
        formStore={formStore}
        initialValues={initialValues}
      />
      {transportAttributes && (
        <OrderTransportViewCard
          hideActions={isDeparted ? true : hideActions}
          allowEdit
          disableInboundTransport={isInboundOrderFulfilled}
          onClickSave={handleSaveTransport}
          attributes={transportAttributes}
          formStore={formStore}
          inboundTransportStore={inboundTransportStore}
          outboundTransportStore={outboundTransportStore}
          onClickEdit={() => setCargoFormIntoEditMode(true)}
        />
      )}

      {!hasServiceOrders && (
        <OrderCargoViewCard
          title={t('cargo', 'Cargo')}
          hideActions={isDeparted ? true : hideActions}
          allowEdit
          onClickSave={handleSaveCommonCargo}
          attributes={cargoAttributes.commonCargoAttributes}
          cargoDetailsFormStore={commonCargoDetailsCargoFormStore}
          directionalCargoDetailsFormStore={commonCargoDetailsFormStore}
          formStore={formStore}
          initialValues={initialValues}
          directionalInitialValues={initialValues?.cargoDetails}
          cargoFormIntoEditMode={cargoFormIntoEditMode}
        />
      )}

      {hasServiceOrders && (
        <Grid container columnSpacing={2}>
          <Grid size={6}>
            <OrderCargoViewCard
              title={t('inboundCargo', 'Inbound cargo')}
              hideActions={isDeparted ? true : hideActions}
              allowEdit
              hideCustoms
              hideDoorDirection
              isSmallView
              onClickSave={handleSaveInboundCargo}
              attributes={{
                ...cargoAttributes.inboundCargoAttributes,
                hideStowageInstruction: true,
              }}
              cargoDetailsFormStore={inboundCargoDetailsCargoFormStore}
              directionalCargoDetailsFormStore={inboundCargoDetailsFormStore}
              formStore={formStore}
              initialValues={initialValues}
              directionalInitialValues={initialValues?.inboundCargoDetails}
              cargoFormIntoEditMode={cargoFormIntoEditMode}
            />
          </Grid>
          <Grid size={6}>
            <OrderCargoViewCard
              title={t('outboundCargo', 'Outbound cargo')}
              hideActions={isDeparted ? true : hideActions}
              allowEdit
              isSmallView
              onClickSave={handleSaveOutboundCargo}
              attributes={cargoAttributes.outboundCargoAttributes}
              cargoDetailsFormStore={outboundCargoDetailsCargoFormStore}
              directionalCargoDetailsFormStore={outboundCargoDetailsFormStore}
              formStore={formStore}
              initialValues={initialValues}
              directionalInitialValues={initialValues?.outboundCargoDetails}
              cargoFormIntoEditMode={cargoFormIntoEditMode}
            />
          </Grid>
        </Grid>
      )}

      <OrderHoldsSection
        store={holdsStore}
        allowEdit
        hideActions={hideActions}
        containerNumber={containerAttributes.number!}
        onClickSave={onSubmit}
      />
    </Stack>
  )
})
